import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Standalone Google OAuth callback. Google redirects the browser back to /auth/google with
// ?code & ?state. We hand them to the backend, which exchanges the code server-side, enforces
// the AVA fleet allowlist, sets the httponly auth cookie, and returns where to land next
// (the OS, or a resumed /api/hive/sso/start handshake for a ship that bounced Doc here).
export default function GoogleAuthCallback() {
  const hasProcessed = useRef(false);
  const [phase, setPhase] = useState("verifying"); // verifying | retrying | denied | failed

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state") || "";
    if (!code) {
      window.location.replace("/");
      return;
    }
    const fail = (p = "failed") => {
      setPhase(p);
      setTimeout(() => window.location.replace("/"), 2800);
    };
    const attempt = (n) => {
      api
        .post("/auth/google/callback", { code, state }, { timeout: 25000 })
        .then((res) => {
          const dest = res.data?.redirect || (res.data?.role === "admin" ? "/os" : "/");
          window.location.replace(dest);
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 403) return fail("denied"); // not on the allowlist - retrying won't help
          if (status === 400 || status === 401) return fail(); // expired/invalid/used code
          // Only transient (network/5xx) failures are retried; the code is NOT consumed in
          // that case, so a retry is safe.
          if (n < 3) {
            setPhase("retrying");
            setTimeout(() => attempt(n + 1), 1200);
          } else {
            fail();
          }
        });
    };
    attempt(1);
  }, []);

  const MESSAGES = {
    verifying: "Verifying Google sign-in...",
    retrying: "Still verifying - re-establishing the secure link...",
    denied: "This Google account is not on the AVA fleet allowlist. Ask Doc to add you.",
    failed: "Sign-in could not be verified - returning to base...",
  };

  const isError = phase === "failed" || phase === "denied";
  return (
    <div className="min-h-screen w-full bg-[#030509] flex items-center justify-center px-6">
      <p
        className={`text-center font-mono text-xs uppercase tracking-[0.2em] ${isError ? "text-rose-300" : "text-cyan-300"}`}
        data-testid="google-auth-callback-status"
      >
        {MESSAGES[phase]}
      </p>
    </div>
  );
}
